if (!customElements.get('option-select')) {
  customElements.define(
    'option-select',
    class OptionSelect extends AnimatedList {
      constructor() {
        super({
          duration: 0.25,
          panelSelector: '.option-select__panel',
          toggleButtonSelector: '.option-select__toggle',
          closeButtonSelector: '.option-select__close',
          backdropSelector: '.option-select__backdrop-none',
          bodyOverflowClass: 'overflow-hidden-mobile',
          searchInputSelector: '.option-select__search-input',
          resetButtonSelector: '.option-select__search-clear',
          focusableItemsSelector: '.option-select__option',
          scrollTrackSelector: 'custom-scrollbar',
          containerSelector: 'body',
          staggerSelector: '.option-select__list li:not([hidden])',
          trapFocus: true,
          initialOpacity: 0,
          staggerDuration: 0.2,
          stageDelay: 0.03
        });

        this.clearButton = this.elements.resetButton;
        this.noResults = this.querySelector('.option-select__no-results');
        this.viewport = this.querySelector('.option-select__list-viewport');
        this.selectedEl = this.querySelector('.option-select__selected');

        this.handleEscape = (event) => {
          if (event.key === 'Escape') this.close();
        };

        this.handleOutside = (event) => {
          const path = event.composedPath ? event.composedPath() : [];
          if (!path.includes(this)) this.close();
        };

        this.syncViewportHeight = () => {
          const vv = window.visualViewport;
          if (!vv) return;
          const bottomOffset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          this.style.setProperty('--option-select-bottom-offset', `${bottomOffset}px`);
          requestAnimationFrame(() => this.elements.scrollTrack?.scheduleThumbUpdate?.(true));
        };

        this.fullscreenQuery = window.matchMedia('(max-width: 576px)');
        this.handleFullscreenChange = () => {
          this._capped = false;
          if (this.viewport) this.viewport.style.maxHeight = '';
          if (this.isOpen) {
            this.positionPanel();
            this.capViewport();
            this.elements.scrollTrack?.scheduleThumbUpdate?.(true);
          }
        };
        this.fullscreenQuery.addEventListener('change', this.handleFullscreenChange);

        this.initSelectEvents();
      }

      get optionLinks() {
        return Array.from(this.querySelectorAll('.option-select__option'));
      }

      get optionItems() {
        return Array.from(this.querySelectorAll('.option-select__item'));
      }

      initSelectEvents() {
        this.elements.searchInput?.addEventListener('input', this.filterOptions.bind(this));
        this.addEventListener('click', (event) => {
          const option = event.target.closest('.option-select__option');
          if (option && this.contains(option)) this.onSelect(option, event);
        });
      }

      get isFullscreen() {
        return window.matchMedia('(max-width: 576px)').matches;
      }

      withMeasurablePanel(callback) {
        const panel = this.elements.panel;
        if (!panel) return;

        const wasHidden = panel.hasAttribute('hidden');
        panel.style.visibility = 'hidden';
        panel.removeAttribute('hidden');

        callback(panel);

        if (wasHidden) panel.setAttribute('hidden', '');
        panel.style.visibility = '';
      }

      capViewport() {
        if (!this.viewport) return;
        if (this.isFullscreen) {
          this.viewport.style.maxHeight = '';
          return;
        }

        const MAX_VISIBLE = 10;
        if (this._capped || this.optionItems.length <= MAX_VISIBLE) return;

        this.withMeasurablePanel(() => {
          const firstTop = this.optionItems[0].getBoundingClientRect().top;
          const lastBottom = this.optionItems[MAX_VISIBLE - 1].getBoundingClientRect().bottom;
          const height = lastBottom - firstTop;
          if (height > 0) {
            this.viewport.style.maxHeight = `${height}px`;
            this._capped = true;
          }
        });
      }

      positionPanel() {
        const panel = this.elements.panel;
        if (!panel) return;

        panel.style.removeProperty('inset-inline-start');
        panel.style.removeProperty('inset-inline-end');

        if (this.isFullscreen) return;

        const container = this.closest('.option-select-bar') || this.closest('.option-select-panel');
        if (!container) return;

        this.withMeasurablePanel((el) => {
          const containerRect = container.getBoundingClientRect();
          const panelRect = el.getBoundingClientRect();

          if (panelRect.right > containerRect.right || panelRect.right > window.innerWidth) {
            panel.style.insetInlineStart = 'auto';
            panel.style.insetInlineEnd = '0';
          }
        });
      }

      normalize(value) {
        return value
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .trim()
          .toLowerCase();
      }

      filterOptions() {
        const query = this.normalize(this.elements.searchInput.value);
        let visible = 0;

        this.clearButton?.classList.toggle('hidden', query.length === 0);

        this.optionItems.forEach((item) => {
          const nameEl = item.querySelector('.option-select__option-name');
          const match = nameEl ? this.normalize(nameEl.textContent).includes(query) : false;
          item.hidden = !match;
          if (match) visible += 1;
        });

        if (this.noResults) this.noResults.hidden = visible !== 0;

        if (this.viewport) this.viewport.scrollTop = 0;
        this.elements.scrollTrack?.scheduleThumbUpdate?.(true);
        this.updateFocusTrap();
      }

      onSelect(option, event) {
        event.preventDefault();

        this.optionLinks.forEach((el) => el.removeAttribute('aria-current'));
        option.setAttribute('aria-current', 'true');

        const nameEl = option.querySelector('.option-select__option-name');
        if (this.selectedEl && nameEl) this.selectedEl.textContent = nameEl.textContent;
        this.classList.add('is-selected');

        this.dispatchEvent(
          new CustomEvent('name-option:select', {
            bubbles: true,
            detail: { value: option.dataset.value }
          })
        );

        this.close();
      }

      open() {
        const scope = this.closest('.option-select-section') || document;
        scope.querySelectorAll('option-select').forEach((el) => {
          if (el !== this && el.isOpen) el.close();
        });

        this.positionPanel();
        this.capViewport();

        const search = this.elements.searchInput;
        this.elements.searchInput = null;
        super.open();
        this.elements.searchInput = search;

        if (this.viewport) this.viewport.scrollTop = 0;

        this._isClosing = false;
        document.addEventListener('keydown', this.handleEscape);
        document.addEventListener('pointerdown', this.handleOutside);

        if (this.isFullscreen) {
          if (window.visualViewport) {
            this.syncViewportHeight();
            window.visualViewport.addEventListener('resize', this.syncViewportHeight);
            window.visualViewport.addEventListener('scroll', this.syncViewportHeight);
          }
          this.querySelector('.option-select__close')?.focus({ preventScroll: true });
        } else {
          const focusTarget =
            search ||
            this.querySelector('.option-select__option[aria-current="true"]') ||
            this.optionLinks[0];
          focusTarget?.focus({ preventScroll: true });
        }
      }

      close() {
        if (this._isClosing) return;
        this._isClosing = true;
        document.removeEventListener('keydown', this.handleEscape);
        document.removeEventListener('pointerdown', this.handleOutside);
        window.visualViewport?.removeEventListener('resize', this.syncViewportHeight);
        window.visualViewport?.removeEventListener('scroll', this.syncViewportHeight);
        this.style.removeProperty('--option-select-bottom-offset');
        super.close();
      }

      animateShowPanel() {
        this.animateShow(
          this.elements.panel,
          {
            heightAnimation: !this.isFullscreen,
            staggerSelector: this.options.staggerSelector,
            staggerLimit: this.isFullscreen ? false : 10,
            initialOpacity: this.options.initialOpacity,
            staggerDuration: this.options.staggerDuration,
            stageDelay: this.options.stageDelay,
            hideOverLimit: true,
            onStart: () => {
              if (this.elements.scrollTrack) this.elements.scrollTrack.style.opacity = '1';
            }
          },
          false
        );
      }

      animateHidePanel() {
        this.animateHide(
          this.elements.panel,
          {
            heightAnimation: false,
            onStart: () => {
              if (this.elements.scrollTrack) this.elements.scrollTrack.style.opacity = '0';
            },
            onComplete: () => this.hidePanel()
          },
          false
        );
      }
    }
  );
}
